// useContractPreview.ts - lifecycle of one staff contract preview: mint a
// preview-only submission, hold it with the request it belongs to, then either
// send that request (the backend voids the preview and mints the merchant's own
// contract) or void it. A preview with editable fields is staff's form to fill:
// `completed` tracks its funder step, and `editAgain` swaps in a fresh mint
// pre-answered from DocuSeal. A failed reload or edit again, or a send the
// preview must fix, leaves the session as it was with `actionError` set. Callers
// own what happens after a failed send; every abort goes through `discard` so no
// unsigned preview is left behind, including when the panel unmounts mid-preview.
import { useCallback, useEffect, useRef, useState } from "react";
import { merchantFlowService } from "../api/merchantFlowService";
import type { ContractPreviewResponse, SendRequest } from "../types";
import { classifySendFailure } from "./useSendFlow";

interface PreviewSession {
  request: SendRequest;
  preview: ContractPreviewResponse;
  /** Staff finished the funder step of this preview's form. */
  completed: boolean;
}

/** `confirm` found no live preview to name: the send must not go out without it. */
export class PreviewLostError extends Error {
  constructor() {
    super("The contract preview is no longer available. Send again to preview it.");
    this.name = "PreviewLostError";
  }
}

type Answers = Record<string, string> | undefined;

/**
 * Best effort: an unreachable void leaves an unsigned row that a later send can
 * supersede. With `answers`, resolves to the preview's DocuSeal answers, if any.
 */
function voidPreview(borrowerId: number, submissionId: number, answers = false): Promise<Answers> {
  const voided = answers
    ? merchantFlowService.cancelContractPreview(borrowerId, submissionId, { answers })
    : merchantFlowService.cancelContractPreview(borrowerId, submissionId);
  return voided.catch(() => undefined);
}

/** Free a PDF preview's object URL (nothing was minted, so nothing is voided). */
function revokePdf(preview: ContractPreviewResponse | undefined): void {
  if (preview?.mode === "pdf" && preview.object_url) URL.revokeObjectURL(preview.object_url);
}

/**
 * A document with no fields previews its own PDF: fetched through the authenticated
 * API and shown from an object URL. Other previews pass through.
 */
async function withPdf(
  borrowerId: number,
  preview: ContractPreviewResponse
): Promise<ContractPreviewResponse> {
  if (preview.mode !== "pdf") return preview;
  const blob = await merchantFlowService.documentPdf(borrowerId, preview.document_id);
  return { ...preview, object_url: URL.createObjectURL(blob) };
}

/** Previous answers for the fields this preview leaves open; untrusted prefill only. */
function withDefaults(preview: ContractPreviewResponse, answers: Answers): ContractPreviewResponse {
  const open = new Set(preview.editable_fields ?? []);
  const kept = Object.entries(answers ?? {}).filter(([name]) => open.has(name));
  return kept.length ? { ...preview, field_defaults: Object.fromEntries(kept) } : preview;
}

function actionMessage(err: unknown): string {
  const failure = classifySendFailure(err);
  return failure.kind === "reprompt"
    ? "The contract preview could not be updated."
    : failure.message;
}

export function useContractPreview(borrowerId: number | undefined) {
  const [session, setSession] = useState<PreviewSession | null>(null);
  // Async callers chain start → send → discard within one closure; the ref is
  // what they read, so none of them acts on a stale session.
  const sessionRef = useRef<PreviewSession | null>(null);
  const mountedRef = useRef(false);
  // A send carrying the preview voids it itself; voiding it on unmount meanwhile
  // would race that void (a second DocuSeal archive can fail the send).
  const sendingRef = useRef(false);
  // A counter, not a flag: `confirm` runs a send that can `discard` inside it.
  const [pending, setPending] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);

  const hold = useCallback((next: PreviewSession | null) => {
    sessionRef.current = next;
    setSession(next);
    setActionError(null);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const left = sessionRef.current;
      // A send carrying it settles the session: `release` on success, `discard` on failure.
      if (!left || sendingRef.current) return;
      sessionRef.current = null;
      revokePdf(left.preview);
      if (borrowerId != null && left.preview.submission_id != null) {
        void voidPreview(borrowerId, left.preview.submission_id);
      }
    };
  }, [borrowerId]);

  const track = useCallback(async <T>(work: () => Promise<T>): Promise<T> => {
    setPending((n) => n + 1);
    try {
      return await work();
    } finally {
      setPending((n) => n - 1);
    }
  }, []);

  /**
   * Mint the preview and open the session; `answers` (from a discarded preview)
   * pre-answer its open fields. Rejects with the API error.
   */
  const start = useCallback(
    (request: SendRequest, answers?: Answers) =>
      track(async () => {
        if (borrowerId == null) return;
        const minted = await merchantFlowService.createContractPreview(borrowerId, request);
        if (minted.submission_id == null) {
          // A PDF preview: nothing to void, only the fetched PDF to free.
          const preview = await withPdf(borrowerId, minted);
          if (!mountedRef.current) {
            revokePdf(preview);
            return;
          }
          hold({ request, preview, completed: false });
          return;
        }
        if (!mountedRef.current) {
          await voidPreview(borrowerId, minted.submission_id);
          return;
        }
        hold({ request, preview: withDefaults(minted, answers), completed: false });
      }),
    [borrowerId, hold, track]
  );

  // Not through `hold`: a form reopened on its "submitted" screen reports completion
  // on load, which must not wipe a send error shown for that same preview.
  const markCompleted = useCallback(() => {
    const current = sessionRef.current;
    if (!current || current.completed) return;
    sessionRef.current = { ...current, completed: true };
    setSession(sessionRef.current);
  }, []);

  /** Swap in a re-minted credential for the same submission. A failure sets `actionError`. */
  const refresh = useCallback(
    () =>
      track(async () => {
        const current = sessionRef.current;
        if (borrowerId == null || !current) return;
        const { submission_id } = current.preview;
        // A PDF preview has no form credential to renew.
        if (submission_id == null) return;
        try {
          const preview = await merchantFlowService.refreshContractPreview(
            borrowerId,
            submission_id
          );
          const held = sessionRef.current;
          if (held?.preview.submission_id !== submission_id) return;
          hold({
            ...held,
            // Edit again's answers live only in this session; a reload keeps them.
            preview: { ...preview, field_defaults: held.preview.field_defaults },
            // Staff may have finished the form in another tab.
            completed: preview.funder_completed ?? held.completed,
          });
        } catch (err) {
          setActionError(actionMessage(err));
        }
      }),
    [borrowerId, hold, track]
  );

  /** Replace the filled preview with one pre-answered from DocuSeal. A failure sets `actionError`. */
  const editAgain = useCallback(
    () =>
      track(async () => {
        const current = sessionRef.current;
        if (borrowerId == null || !current) return;
        // A PDF preview has no answers to edit.
        if (current.preview.submission_id == null) return;
        try {
          // The server voids the old submission once the new one is minted.
          const preview = await merchantFlowService.editContractPreview(
            borrowerId,
            current.preview.submission_id,
            current.request
          );
          if (!mountedRef.current) {
            if (preview.submission_id != null) await voidPreview(borrowerId, preview.submission_id);
            return;
          }
          hold({ request: current.request, preview, completed: false });
        } catch (err) {
          setActionError(actionMessage(err));
        }
      }),
    [borrowerId, hold, track]
  );

  /** Close the session without voiding (the send that carried it voided it). */
  const release = useCallback(() => {
    revokePdf(sessionRef.current?.preview);
    hold(null);
  }, [hold]);

  /**
   * Close the session and void its unsent submission (best effort). Asked for
   * `answers`, resolves to its DocuSeal answers, for a re-mint to pre-answer.
   */
  const discard = useCallback(
    ({ answers = false }: { answers?: boolean } = {}) =>
      track(async (): Promise<Answers> => {
        const submissionId = sessionRef.current?.preview.submission_id;
        revokePdf(sessionRef.current?.preview);
        hold(null);
        if (borrowerId == null || submissionId == null) return undefined;
        return voidPreview(borrowerId, submissionId, answers);
      }),
    [borrowerId, hold, track]
  );

  /**
   * Run `send` with the previewed request; the send voids the preview it names.
   * Rejects with `PreviewLostError`, without sending, when no preview is live.
   */
  const confirm = useCallback(
    <T>(send: (request: SendRequest) => Promise<T>) =>
      track(async () => {
        const current = sessionRef.current;
        if (!current) throw new PreviewLostError();
        sendingRef.current = true;
        try {
          return await send({
            ...current.request,
            preview_submission_id: current.preview.submission_id,
          });
        } finally {
          sendingRef.current = false;
        }
      }),
    [track]
  );

  return {
    preview: session?.preview ?? null,
    completed: session?.completed ?? false,
    actionError,
    showError: setActionError,
    busy: pending > 0,
    start,
    markCompleted,
    refresh,
    editAgain,
    release,
    discard,
    confirm,
  };
}
